import type { WidgetId } from '../../selfOwnedWidget';
import type { WidgetHostContainer } from '../Container';
import { computed, createModel } from '@preact/signals-react';
import type { Model, ModelConstructor, ReadonlySignal } from '@preact/signals-react';
import type { Logger } from '@thinkalpha/ui-ioc/core/types/Logger';
import type { ByIdState } from 'src/store/reducers/tab/byId';
import type { ContainerViewModel, WidgetTabViewModel } from 'src/store/types';
import type { EventBus } from 'src/types/EventBus/EventBus';
import { EventBusBuilder } from 'src/types/EventBus/EventBus';
import type {
    ChangeTabEvent,
    ChangeTabNameEvent,
    ContainerActiveTabSignal,
    ContainerSignal,
    DeleteTabEvent,
    DeleteWidgetEvent,
    IsMaximizedSignal,
    NameSignal,
    TabIdSignal,
    TabsByIdSignal,
    ToggleMaximizedEvent,
    WidgetTypeSignal,
} from 'src/types/EventBus/index';

export type ContainerWidgetTabSliceSignals = TabIdSignal &
    WidgetTypeSignal &
    NameSignal &
    IsMaximizedSignal &
    ContainerSignal &
    ContainerActiveTabSignal &
    TabsByIdSignal;

export type ContainerWidgetTabSliceEvents = ToggleMaximizedEvent &
    DeleteWidgetEvent &
    ChangeTabEvent &
    DeleteTabEvent &
    ChangeTabNameEvent;

export type ContainerWidgetTabSliceEventBus = EventBus<ContainerWidgetTabSliceSignals, ContainerWidgetTabSliceEvents>;

function tabToViewModel(
    tab: { id: string; name?: string | null; widget: { type: string; channelId?: string | null } },
    containerId: string,
    isActive: boolean,
): WidgetTabViewModel {
    return {
        id: tab.id,
        name: tab.name,
        widget: tab.widget as never,
        containerId,
        channelId: tab.widget.channelId ?? null,
        isActive,
    } as WidgetTabViewModel;
}

interface ContainerWidgetTabSliceInit {
    container: WidgetHostContainer;
    tabId: WidgetId;
    logger: Logger;
}

export interface ContainerWidgetTabSliceModel {
    readonly tabId: ReadonlySignal<string>;
    readonly widgetType: ReadonlySignal<string>;
    readonly name: ReadonlySignal<string>;
    readonly isMaximized: ReadonlySignal<boolean>;
    readonly containerVm: ReadonlySignal<ContainerViewModel>;
    readonly containerActiveTab: ReadonlySignal<WidgetTabViewModel | undefined>;
    readonly tabsById: ReadonlySignal<ByIdState>;
    readonly eventBus: ContainerWidgetTabSliceEventBus;
}

export const ContainerWidgetTabSlice: ModelConstructor<ContainerWidgetTabSliceModel, [ContainerWidgetTabSliceInit]> =
    createModel<ContainerWidgetTabSliceModel, [ContainerWidgetTabSliceInit]>((init: ContainerWidgetTabSliceInit) => {
        const ownContainerId: ReadonlySignal<string> = computed(() => {
            const lc = init.container.lifecycle.value;
            return lc.kind === 'initialized' ? lc.id : '';
        });

        const tabId: ReadonlySignal<string> = computed(() => init.tabId);

        const widgetType: ReadonlySignal<string> = computed(() => {
            const tab = init.container.tabs.value.find((t) => t.id === init.tabId);
            return tab?.widget.type ?? '';
        });

        const name: ReadonlySignal<string> = computed(() => {
            const tab = init.container.tabs.value.find((t) => t.id === init.tabId);
            return tab?.name ?? '';
        });

        const isMaximized: ReadonlySignal<boolean> = computed(() => init.container.isMaximized.value);

        const containerVm: ReadonlySignal<ContainerViewModel> = computed(() => ({
            id: ownContainerId.value,
            activeTabId: init.container.activeTabId.value,
            tabIds: init.container.tabs.value.map((t) => t.id),
        }));

        const containerActiveTab: ReadonlySignal<WidgetTabViewModel | undefined> = computed(() => {
            const activeId = init.container.activeTabId.value;
            const tabs = init.container.tabs.value;
            // Fall back to tabs[0] when activeId is null OR points to a removed
            // tab - `find` would otherwise return undefined and consumers reading
            // .id/.name would crash mid-removal.
            const activeTab = (activeId !== null ? tabs.find((t) => t.id === activeId) : undefined) ?? tabs[0];
            if (!activeTab) {
                // Container with zero tabs is a transient state during
                // construction or final-tab removal.
                return undefined;
            }
            return tabToViewModel(activeTab, ownContainerId.value, true);
        });

        const tabsById: ReadonlySignal<ByIdState> = computed(() => {
            const map: ByIdState = {};
            const containerId = ownContainerId.value;
            const activeId = init.container.activeTabId.value;
            for (const tab of init.container.tabs.value) {
                map[tab.id] = tabToViewModel(tab, containerId, tab.id === activeId);
            }
            return map;
        });

        const eventBus: ContainerWidgetTabSliceEventBus = EventBusBuilder.create()
            .addSignal('widget::tabId', () => tabId)
            .addSignal('widget::type', () => widgetType)
            .addSignal('widget::name', () => name)
            .addSignal('widget::isMaximized', () => isMaximized)
            .addSignal('widget::container', () => containerVm)
            .addSignal('widget::containerActiveTab', () => containerActiveTab)
            .addSignal('widget::tabsById', () => tabsById)
            .addEvent('widget::toggleMaximized', () => {
                init.container.toggleMaximized();
            })
            .addEvent('widget::delete', () => {
                // The header X removes the whole container (every tab), matching
                // the legacy WidgetTabSlice; widget::deleteTab removes single tabs.
                init.container.deleteSelf().catch((err: unknown) => {
                    init.logger.error('Container.deleteSelf failed during widget::delete', { error: err });
                });
            })
            .addEvent('widget::changeTab', ({ tabId: nextTabId }) => {
                init.container.setActiveTab(nextTabId as WidgetId);
            })
            .addEvent('widget::deleteTab', ({ tabKey }) => {
                init.container.removeWidget(tabKey as WidgetId).catch((err: unknown) => {
                    init.logger.error('Container.removeWidget failed during widget::deleteTab', { error: err });
                });
            })
            .addEvent('widget::changeTabName', ({ tab, name: nextName }) => {
                init.container.setTabName(tab.id as WidgetId, nextName);
            })
            .build();

        return {
            tabId,
            widgetType,
            name,
            isMaximized,
            containerVm,
            containerActiveTab,
            tabsById,
            eventBus,
        };
    });

export type ContainerWidgetTabSlice = Model<ContainerWidgetTabSliceModel>;
